<template>
  <div id="app">
    <header class="app-header">
      <HeaderVue />
      <nav class="main-nav">
        <!-- 桌面端菜单 -->
        <el-menu class="desktop-menu" :default-active="activeIndex" mode="horizontal" :router="true">
          <template v-for="(item, index) in routerList" :key="index">
            <template v-if="!item.children">
              <el-menu-item :index="item.path">
                {{ item.name }}
              </el-menu-item>
            </template>
            <template v-else>
              <el-sub-menu :index="item.path">
                <template #title>{{ item.name }}</template>
                <template v-for="(child, childIndex) in item.children" :key="childIndex">
                  <template v-if="!child.children">
                    <el-menu-item :index="child.path">
                      {{ child.name }}
                    </el-menu-item>
                  </template>
                  <template v-else>
                    <el-sub-menu :index="child.path">
                      <template #title>{{ child.name }}</template>
                      <el-menu-item v-for="(grandChild, grandIndex) in child.children" :key="grandIndex"
                        :index="grandChild.path">
                        {{ grandChild.name }}
                      </el-menu-item>
                    </el-sub-menu>
                  </template>
                </template>
              </el-sub-menu>
            </template>
          </template>
        </el-menu>
      </nav>
    </header>

    <!-- 移动端隐藏式悬浮按钮 -->
    <div class="mobile-menu-handle" :class="{ 'active': isMobileMenuOpen }"
      @touchstart="handleTouchStart" v-show="isMobile">
      <div class="mobile-menu-floating-btn" @click="toggleMobileMenu">
        <div class="hamburger">
          <span class="line line1"></span>
          <span class="line line2"></span>
          <span class="line line3"></span>
        </div>
      </div>
    </div>

    <!-- 移动端菜单 (从左侧滑出) -->
    <transition name="slide-fade">
      <div class="mobile-menu-wrapper" v-show="isMobileMenuOpen && isMobile" @click.self="closeMobileMenu">
        <div class="mobile-menu-container" :class="{ 'active': isMobileMenuOpen }">
          <div class="menu-header">
            <h3>网站导航</h3>
            <el-icon class="close-icon" @click="closeMobileMenu">
              <Close />
            </el-icon>
          </div>
          <el-menu class="mobile-menu" :default-active="activeIndex" :router="true" background-color="#f8fafc"
            text-color="#334155" active-text-color="#1373C0">
            <template v-for="(item, index) in routerList" :key="index">
              <template v-if="!item.children">
                <el-menu-item :index="item.path" @click="closeMobileMenu">
                  <span>{{ item.name }}</span>
                </el-menu-item>
              </template>
              <template v-else>
                <el-sub-menu :index="item.path">
                  <template #title>
                    <span>{{ item.name }}</span>
                  </template>
                  <template v-for="(child, childIndex) in item.children" :key="childIndex">
                    <template v-if="!child.children">
                      <el-menu-item :index="child.path" @click="closeMobileMenu">
                        <span>{{ child.name }}</span>
                      </el-menu-item>
                    </template>
                    <template v-else>
                      <el-sub-menu :index="child.path">
                        <template #title>
                          <span>{{ child.name }}</span>
                        </template>
                        <el-menu-item v-for="(grandChild, grandIndex) in child.children" :key="grandIndex"
                          :index="grandChild.path" @click="closeMobileMenu">
                          <span>{{ grandChild.name }}</span>
                        </el-menu-item>
                      </el-sub-menu>
                    </template>
                  </template>
                </el-sub-menu>
              </template>
            </template>
          </el-menu>
        </div>
      </div>
    </transition>

    <main class="app-main">
      <router-view />
    </main>

    <footer class="app-footer">
      <FooterVue />
    </footer>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import HeaderVue from './components/public/header.vue';
import FooterVue from './components/public/footer.vue';
import { routerList } from '@/pubilc/data';
import emitter from '@/plugins/emitter';
import { Close } from '@element-plus/icons-vue';

const route = useRoute();
const activeIndex = ref(route.path);
const isMobileMenuOpen = ref(false);
const isMobile = ref(false);
const touchStartX = ref(0);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

const updateActiveIndex = () => {
  activeIndex.value = route.path;
};

const handleTouchStart = (e: TouchEvent) => {
  touchStartX.value = e.touches[0].clientX;
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  const handle = document.querySelector('.mobile-menu-handle');
  const menuWrapper = document.querySelector('.mobile-menu-wrapper');

  if (handle && !handle.contains(event.target as Node) &&
    menuWrapper && !menuWrapper.contains(event.target as Node)) {
    closeMobileMenu();
  }
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
  document.addEventListener('click', handleClickOutside);
  emitter.on('route-change', updateActiveIndex);
  updateActiveIndex();

  // 触摸滑动显示控制
  document.addEventListener('touchmove', (e) => {
    if (touchStartX.value > 0 && e.touches[0].clientX - touchStartX.value > 30) {
      // 保持按钮显示
    }
  });

  document.addEventListener('touchend', () => {
    touchStartX.value = 0;
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile);
  document.removeEventListener('click', handleClickOutside);
  emitter.off('route-change', updateActiveIndex);
});
</script>

<style lang="scss" scoped>
html,
body,
#app {
  width: 100%;
  max-width: 100%;
  overflow-x: visible;
}

#app {
  display: grid;
  grid-template-areas:
    "header"
    "main"
    "footer";
  grid-template-rows: auto 1fr auto;
  min-height: 100vh;
  width: 100%;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  position: relative;
}

.app-header {
  grid-area: header;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-image: url('@/assets/public/background.png');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  position: relative;
}

.main-nav {
  background-color: #1373C0;

  .desktop-menu {
    display: flex;
    justify-content: center;
    background-color: transparent;
    border-bottom: none;

    :deep(.el-menu-item),
    :deep(.el-sub-menu__title) {
      color: white;
      height: 60px;
      line-height: 60px;
      font-size: 16px;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }

    :deep(.el-menu-item.is-active) {
      background-color: rgba(255, 255, 255, 0.2);
    }

    :deep(.el-sub-menu__icon-arrow) {
      color: white;
    }

    :deep(.el-sub-menu) {
      .el-menu {
        min-width: 200px;

        .el-sub-menu {
          .el-menu {
            position: absolute;
            left: 100%;
            top: 0;
            min-width: 200px;
            margin-left: 4px;
          }
        }
      }
    }
  }
}

/* 移动端隐藏式悬浮按钮容器 */
.mobile-menu-handle {
  position: fixed;
  right: 0;
  top: 10%;
  transform: translateY(-50%);
  z-index: 999;
  transition: all 0.3s ease;
}

/* 悬浮按钮主体 */
.mobile-menu-floating-btn {
  width: 44px;
  height: 44px;
  background-color: rgba(19, 115, 192, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(19, 115, 192, 1);
    transform: scale(1.05);
  }

  .hamburger {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 20px;
    height: 16px;
    transition: all 0.3s ease;

    .line {
      display: block;
      height: 2px;
      width: 100%;
      background-color: white;
      transition: all 0.3s ease;

      &.line1 {
        transform-origin: 0% 0%;
      }

      &.line2 {
        transform-origin: 0% 50%;
      }

      &.line3 {
        transform-origin: 0% 100%;
      }
    }
  }

  &.active .hamburger {
    .line1 {
      transform: rotate(45deg) translate(4px, 4px);
    }

    .line2 {
      opacity: 0;
    }

    .line3 {
      transform: rotate(-45deg) translate(4px, -4px);
    }
  }
}

/* 移动端菜单 */
.mobile-menu-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 998;
  display: flex;
  justify-content: flex-start;
}

.mobile-menu-container {
  width: 85%;
  max-width: 300px;
  height: 100%;
  background-color: #f8fafc;
  overflow-y: auto;
  box-shadow: 2px 0 15px rgba(0, 0, 0, 0.1);
  transform: translateX(-100%);
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);

  &.active {
    transform: translateX(0);
  }

  .menu-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background-color: #1373C0;
    color: white;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
    }

    .close-icon {
      font-size: 20px;
      cursor: pointer;
      color: rgba(255, 255, 255, 0.8);
      transition: all 0.3s;

      &:hover {
        color: white;
        transform: scale(1.1);
      }
    }
  }

  .mobile-menu {
    border-right: none;

    :deep(.el-menu-item),
    :deep(.el-sub-menu__title) {
      height: 50px;
      line-height: 50px;
      font-size: 15px;
      transition: all 0.2s;

      &:hover {
        background-color: #e2e8f0 !important;
        color: #1373C0 !important;
      }
    }

    :deep(.el-menu-item.is-active) {
      color: #1373C0 !important;
      background-color: #e2e8f0 !important;
      font-weight: 500;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 4px;
        background-color: #1373C0;
      }
    }

    :deep(.el-sub-menu) {
      .el-menu {
        background-color: #f1f5f9 !important;

        .el-menu-item {
          padding-left: 30px !important;
          font-size: 14px;
        }

        .el-sub-menu {
          .el-menu {
            background-color: #e2e8f0 !important;

            .el-menu-item {
              padding-left: 50px !important;
              font-size: 13px;
            }
          }
        }
      }

      &:deep(.el-sub-menu__title) {
        &:hover {
          background-color: #e2e8f0 !important;
        }
      }
    }

    :deep(.el-sub-menu__icon-arrow) {
      color: #64748b;
    }
  }
}

.app-main {
  grid-area: main;
}

.app-footer {
  grid-area: footer;
}

/* 动画效果 */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.25s ease-out;

  .mobile-menu-container {
    transition: transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
  }
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;

  .mobile-menu-container {
    transform: translateX(-100%);
  }
}

.main-nav .desktop-menu {
  display: flex;
}
.mobile-menu-handle {
  display: none;
}

/* 响应式设计 */
@media (max-width: 1000px) {
  .main-nav .desktop-menu {
    /* 可以调整菜单项间距等，但保持显示 */
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .main-nav .desktop-menu {
    display: none;
  }
  .mobile-menu-handle {
    display: block;
  }
}

@media (min-width: 1001px) {
  .mobile-menu-handle,
  .mobile-menu-wrapper {
    display: none;
  }
  
  .main-nav .desktop-menu {
    display: flex;
  }
}

/* 触摸设备优化 */
@media (hover: none) {
  .mobile-menu-handle {
    right: 0;

    .mobile-menu-floating-btn {
      width: 44px;
      height: 44px;
      opacity: 1;
    }
  }
}

@media (max-width: 480px) {
  .mobile-menu-container {
    width: 90%;
  }
  
  .mobile-menu-floating-btn {
    width: 40px;
    height: 40px;
  }
  
  .hamburger {
    width: 18px;
    height: 14px;
  }
}
</style>